/**
 * The reader's key prompt.
 *
 * Styled as a page of the book rather than as app chrome: the same warm paper,
 * ink and ornament colors the reader is already looking at. A system dialog here
 * would break the spell at the one moment the reader is most inside the text.
 */

import { Button, CircularProgress, Dialog, DialogActions, DialogContent, TextField, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'

import { READER_PAPER_BACKGROUND_SX } from '../../styles/paperBackground'
import { decrypt } from '../../utils/encryption'
import { logger } from '../../utils/logger'

interface DecryptionModalProps {
  open: boolean
  onClose: () => void
  encryptedContent: string
  onDecryptSuccess: (decryptedText: string, mnemonic: string) => void
  /** The book's direction — drives both the layout flow and the language of the copy. */
  direction?: 'rtl' | 'ltr'
  /** The book's title font, so the prompt is set in the same hand as its headings. */
  titleFontFamily?: string
}

const COPY = {
  rtl: {
    title: 'הקטע הזה מוצפן',
    prompt: 'יש לך את המפתח?',
    wrong: 'לא המפתח הזה.',
    cancel: 'לא עכשיו',
    open: 'פתח',
    opening: 'פותח…',
  },
  ltr: {
    title: 'This passage is encrypted',
    prompt: 'Have the key?',
    wrong: 'Not this key.',
    cancel: 'Not now',
    open: 'Open',
    opening: 'Opening…',
  },
} as const

export const DecryptionModal: React.FC<DecryptionModalProps> = ({
  open,
  onClose,
  encryptedContent,
  onDecryptSuccess,
  direction = 'ltr',
  titleFontFamily,
}) => {
  const [keyMnemonic, setKeyMnemonic] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const t = COPY[direction]

  useEffect(() => {
    if (open) {
      setKeyMnemonic('')
      setError('')
    }
  }, [open])

  const handleDecrypt = async () => {
    if (!keyMnemonic) {
      return
    }

    setLoading(true)
    setError('')

    try {
      const result = decrypt(encryptedContent, keyMnemonic)

      if (result.success) {
        onDecryptSuccess(result.text, keyMnemonic)
        onClose()
      } else {
        setError(t.wrong)
      }
    } catch (err) {
      setError(t.wrong)
      logger.error('Decryption error:', err)
    } finally {
      setLoading(false)
    }
  }

  // Enter submits. The field is deliberately single-line: a key is one line, and
  // a newline in it would only ever be a typo that fails to decrypt.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') {
      return
    }
    e.preventDefault()
    if (!loading && keyMnemonic) {
      handleDecrypt()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      dir={direction}
      BackdropProps={{ sx: { backgroundColor: 'rgba(58, 46, 34, 0.42)', backdropFilter: 'blur(2px)' } }}
      PaperProps={{
        sx: {
          ...READER_PAPER_BACKGROUND_SX,
          borderRadius: '10px',
          border: '1px solid var(--reader-sidebar-divider)',
          boxShadow: '0 24px 60px rgba(58, 46, 34, 0.28)',
          px: 1,
          py: 2,
        },
      }}
    >
      <DialogContent sx={{ pb: 1 }}>
        <Typography
          sx={{
            fontFamily: titleFontFamily || 'inherit',
            fontSize: '1.35rem',
            lineHeight: 1.5,
            color: 'var(--reader-text-primary)',
            textAlign: 'center',
          }}
        >
          {t.title}
        </Typography>

        <Typography
          sx={{
            mt: 0.75,
            mb: 2.5,
            fontSize: '0.9rem',
            color: 'var(--reader-text-muted)',
            textAlign: 'center',
          }}
        >
          {error || t.prompt}
        </Typography>

        <TextField
          fullWidth
          variant="standard"
          value={keyMnemonic}
          onChange={(e) => {
            setKeyMnemonic(e.target.value)
            if (error) {
              setError('')
            }
          }}
          onKeyDown={handleKeyDown}
          disabled={loading}
          error={!!error}
          autoFocus
          sx={{
            '& .MuiInput-root': {
              fontSize: '1rem',
              color: 'var(--reader-text-primary)',
            },
            '& .MuiInput-input': { textAlign: 'center', py: 1 },
            // A ruled line to write on, not a boxed form field.
            '& .MuiInput-root:before': { borderBottom: '1px solid var(--reader-sidebar-divider)' },
            '& .MuiInput-root:hover:not(.Mui-disabled):before': {
              borderBottom: '1px solid var(--ornament-stroke)',
            },
            '& .MuiInput-root:after': { borderBottom: '1px solid var(--ornament-fill)' },
            '& .MuiInput-root.Mui-error:after': { borderBottom: '1px solid var(--ornament-fill)' },
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 1, justifyContent: 'center', gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          disableElevation
          sx={{
            color: 'var(--reader-text-muted)',
            textTransform: 'none',
            fontSize: '0.9rem',
            '&:hover': { bgcolor: 'var(--reader-hover)' },
          }}
        >
          {t.cancel}
        </Button>
        <Button
          onClick={handleDecrypt}
          disabled={loading || !keyMnemonic}
          disableElevation
          startIcon={loading ? <CircularProgress size={14} sx={{ color: 'var(--reader-spinner)' }} /> : null}
          sx={{
            color: 'var(--reader-text-primary)',
            textTransform: 'none',
            fontSize: '0.9rem',
            px: 2.5,
            border: '1px solid var(--reader-sidebar-divider)',
            borderRadius: '6px',
            '&:hover': { bgcolor: 'var(--reader-hover)', borderColor: 'var(--ornament-stroke)' },
            '&.Mui-disabled': { color: 'var(--reader-text-submuted)', borderColor: 'var(--reader-sidebar-divider)' },
          }}
        >
          {loading ? t.opening : t.open}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DecryptionModal
